// Auto-generated JSON-LD schemas for every SEO page.
//
// The SEO Engine ships a baseline jsonLd object per page (e.g. Place+Offer for
// property_detail, Article for guides). This module derives the additional
// schemas every page should expose to LLMs and search engines:
//
//   - FAQPage (whenever page.faq has items)
//   - BreadcrumbList (from the visual breadcrumb)
//   - Article enrichment (publisher, author, dates) for guide pages
//   - Dataset (for property_detail, since each riad listing IS structured data)
//
// All schemas are returned as plain JSON objects and then injected via
// SeoHead/Helmet as <script type="application/ld+json"> tags.

import type { SeoFaqItem, SeoPage } from "./seo-engine";
import { DATA_PERIOD } from "./seo-methodology";

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
}

type JsonLd = Record<string, unknown>;

function buildOrgPublisher(siteOrigin: string): JsonLd {
  return {
    "@type": "Organization",
    name: "RiadIntel",
    url: siteOrigin.replace(/\/$/, ""),
  };
}

// ---------- WebSite + Organization (home page schemas) ----------

export function buildWebSiteJsonLd(siteOrigin: string): JsonLd {
  const origin = siteOrigin.replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "RiadIntel",
    url: origin,
    description:
      "Riad investment intelligence for Marrakech — pricing, risk, and best-use analysis across the medina.",
    inLanguage: ["en", "fr", "es", "de", "nl", "ru"],
    publisher: buildOrgPublisher(siteOrigin),
  };
}

export function buildOrganizationJsonLd(siteOrigin: string): JsonLd {
  const origin = siteOrigin.replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RiadIntel",
    alternateName: "Riadata",
    url: origin,
    logo: `${origin}/apple-touch-icon.png`,
    image: `${origin}/opengraph.jpg`,
    description:
      "RiadIntel is an independent riad investment intelligence platform covering Marrakech's medina market with fair-value pricing, expected guesthouse yield, renovation effort and best-use analysis.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Marrakech",
      addressRegion: "Marrakech-Safi",
      addressCountry: "MA",
    },
    areaServed: {
      "@type": "City",
      name: "Marrakech",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Marrakech",
        addressCountry: "MA",
      },
    },
    knowsAbout: [
      "Marrakech medina riads",
      "Riad investment analysis",
      "Riad fair value pricing",
      "Guesthouse yield modelling",
      "Renovation cost estimation",
    ],
    knowsLanguage: ["en", "fr", "es", "de", "nl", "ru"],
    sameAs: ["https://twitter.com/RiadIntel"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "hello@riadintel.com",
        url: `${origin}/contact`,
        availableLanguage: ["English", "French", "Spanish", "German", "Dutch", "Russian"],
      },
    ],
  };
}

function absoluteUrl(siteOrigin: string, path: string | undefined): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  const origin = siteOrigin.replace(/\/$/, "");
  return `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
}

// ---------- FAQPage ----------

/**
 * Core FAQ builder. Accepts any array of plain {question, answer} pairs and
 * returns a valid schema.org FAQPage JSON-LD object, or null when the array
 * is empty. Used directly by research pages that supply Q&A pairs inline.
 */
export function buildFaqJsonLd(
  items: { question: string; answer: string }[],
): JsonLd | null {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/**
 * Adapter for pages that receive FAQ items from the SEO engine (SeoFaqItem[]).
 * Delegates to buildFaqJsonLd so both paths share the same output shape.
 */
export function buildFaqPageJsonLd(faq: SeoFaqItem[] | undefined | null): JsonLd | null {
  if (!faq || faq.length === 0) return null;
  return buildFaqJsonLd(faq.map((item) => ({ question: item.question, answer: item.answer })));
}

// ---------- BreadcrumbList ----------

export function buildBreadcrumbJsonLd(
  crumbs: BreadcrumbCrumb[] | undefined | null,
  siteOrigin: string,
  currentPath?: string,
  localizePath: (p: string) => string = (p) => p,
): JsonLd | null {
  if (!crumbs || crumbs.length === 0) return null;
  const items = crumbs
    .map((c, i) => {
      const item: JsonLd = {
        "@type": "ListItem",
        position: i + 1,
        name: c.label,
      };
      // App breadcrumbs use unprefixed (English) hrefs like "/" and "/r".
      // Localise them so non-English pages don't leak English URLs into the
      // BreadcrumbList schema. The last crumb falls back to currentPath
      // which is already locale-prefixed by the caller.
      const rawHref =
        c.href ?? (i === crumbs.length - 1 ? currentPath : undefined);
      const localised =
        c.href !== undefined ? localizePath(c.href) : rawHref;
      const abs = absoluteUrl(siteOrigin, localised);
      if (abs) item.item = abs;
      return item;
    });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// ---------- Article enrichment ----------
//
// The engine sends a minimal Article jsonLd for guide pages. We enrich it
// with publisher, author, datePublished, dateModified, mainEntityOfPage and
// image — without overwriting any field the engine already set.

function enrichArticleObject(
  base: JsonLd,
  page: SeoPage,
  siteOrigin: string,
  localizedCanonical?: string,
): JsonLd {
  const enriched: JsonLd = { ...base };

  if (!enriched["@context"]) enriched["@context"] = "https://schema.org";
  if (!enriched["@type"]) enriched["@type"] = "Article";

  if (!enriched.publisher) enriched.publisher = buildOrgPublisher(siteOrigin);
  if (!enriched.author) enriched.author = buildOrgPublisher(siteOrigin);

  if (!enriched.datePublished && page.lastPublishedAt) {
    enriched.datePublished = page.lastPublishedAt;
  }
  if (page.lastDataRefreshAt) {
    enriched.dateModified = page.lastDataRefreshAt;
  } else if (!enriched.dateModified) {
    // Only fall back to engine-supplied dates so server-rendered HTML and
    // any client re-render stay byte-identical (no `new Date()` here — that
    // would change between SSR and CSR and trigger hydration mismatches /
    // unstable schema).
    const fallback = page.lastDataRefreshAt ?? page.lastPublishedAt;
    if (fallback) enriched.dateModified = fallback;
  }
  if (
    typeof enriched.datePublished === "string" &&
    typeof enriched.dateModified === "string" &&
    Date.parse(enriched.dateModified) < Date.parse(enriched.datePublished)
  ) {
    enriched.dateModified = enriched.datePublished;
  }

  // Prefer the locale-prefixed canonical (`localizedCanonical`) when the
  // caller provided it. Fall back to the engine canonical (English) only
  // when no localised URL is available, then to the unprefixed slug path.
  const canonical =
    localizedCanonical ??
    page.canonicalUrl ??
    absoluteUrl(siteOrigin, `/r/${page.slug}`);
  if (canonical) {
    if (!enriched.mainEntityOfPage) {
      enriched.mainEntityOfPage = {
        "@type": "WebPage",
        "@id": canonical,
      };
    }
    if (!enriched.url) enriched.url = canonical;
  }

  if (!enriched.headline && page.h1) enriched.headline = page.h1;
  if (!enriched.description && page.metaDescription) {
    enriched.description = page.metaDescription;
  }

  const ogImg =
    absoluteUrl(siteOrigin, page.ogImageUrl ?? undefined) ??
    absoluteUrl(siteOrigin, "/opengraph.jpg");
  if (ogImg && !enriched.image) enriched.image = ogImg;

  return enriched;
}

function isArticleLike(obj: JsonLd): boolean {
  const t = obj["@type"];
  if (typeof t !== "string") return false;
  return /^(Article|NewsArticle|BlogPosting|TechArticle|ScholarlyArticle)$/i.test(t);
}

// ---------- QAPage (answer pages) ----------
//
// Schema.org QAPage is the canonical schema for a page whose primary
// content is a single question and its accepted answer. Google has
// historically used QAPage to surface direct-answer rich results.
// The engine emits Article + FAQPage + Dataset for answer pages but not
// QAPage; we add it here so the page advertises its true intent to LLMs
// and search engines.

function buildQaPageJsonLd(
  page: SeoPage,
  siteOrigin: string,
  localizedCanonical?: string,
): JsonLd | null {
  const question = page.h1 ?? page.title;
  const answer = page.introText ?? page.metaDescription;
  if (!question || !answer) return null;
  const canonical =
    localizedCanonical ??
    page.canonicalUrl ??
    absoluteUrl(siteOrigin, `/r/${page.slug}`);
  return {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: question,
      text: question,
      answerCount: 1,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
        url: canonical,
      },
    },
  };
}

// ---------- Dataset (property pages) ----------

function buildDatasetForProperty(
  page: SeoPage,
  siteOrigin: string,
  localizedCanonical?: string,
): JsonLd {
  const canonical =
    localizedCanonical ??
    page.canonicalUrl ??
    absoluteUrl(siteOrigin, `/r/${page.slug}`);
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: page.h1 ?? page.title,
    description:
      page.metaDescription ||
      `Structured riad market record published by RiadIntel, including pricing intelligence, best-use analysis and execution profile.`,
    url: canonical,
    isAccessibleForFree: true,
    creator: buildOrgPublisher(siteOrigin),
    publisher: buildOrgPublisher(siteOrigin),
    license: "https://riadintel.com/terms",
    keywords: [
      "Marrakech riad",
      "riad investment",
      "riad pricing",
      page.pageType,
    ],
    variableMeasured: [
      "Asking price (MAD)",
      "Price per m² (MAD)",
      "Built surface (m²)",
      "Best use",
      "Renovation class",
      "Execution difficulty",
      "District benchmark median price/m²",
    ],
    distribution: canonical
      ? [
          {
            "@type": "DataDownload",
            encodingFormat: "text/html",
            contentUrl: canonical,
          },
        ]
      : undefined,
    temporalCoverage: DATA_PERIOD,
  };
}

// ---------- Public composer ----------

function typeOf(obj: JsonLd): string | null {
  const t = obj["@type"];
  return typeof t === "string" ? t : null;
}

/**
 * Returns the engine-provided jsonLd PLUS all derived schemas (FAQPage,
 * BreadcrumbList, enriched Article, Dataset for properties).
 *
 * Order matters: search engines and LLMs typically pick the first matching
 * schema for a given page, so we put the engine's primary schema first.
 *
 * Derived schemas are deduped against the engine's @type list so that if the
 * engine ever starts shipping FAQPage/BreadcrumbList/Dataset itself, we
 * defer to it instead of producing duplicates.
 */
export function composePageJsonLd({
  page,
  breadcrumbs,
  siteOrigin,
  currentPath,
  localizePath = (p) => p,
}: {
  page: SeoPage;
  breadcrumbs?: BreadcrumbCrumb[] | null;
  siteOrigin: string;
  /** Locale-prefixed path of the current route (e.g. "/fr/r/<slug>"). */
  currentPath?: string;
  /** Prefix-with-locale helper used to localise breadcrumb hrefs. */
  localizePath?: (p: string) => string;
}): JsonLd[] {
  const out: JsonLd[] = [];

  // Locale-prefixed canonical URL for this page. SeoHead always provides
  // `currentPath` already prefixed with the active locale, so this string
  // is the correct per-locale URL to use across every derived schema.
  const localizedCanonical = currentPath
    ? absoluteUrl(siteOrigin, currentPath)
    : undefined;

  // 1. Engine-provided jsonLd (Place+Offer / Article / ItemList) — enrich
  //    Article-like entries with publisher/dates/etc.
  const baseRaw = page.jsonLd;
  const baseArr: JsonLd[] = baseRaw
    ? Array.isArray(baseRaw)
      ? (baseRaw as JsonLd[])
      : [baseRaw as JsonLd]
    : [];
  const engineTypes = new Set<string>();
  let keptArticle = false;
  for (const obj of baseArr) {
    if (obj && typeof obj === "object") {
      const t = typeOf(obj);
      // Engine payloads may wrap a second Article/FAQ/Breadcrumb set inside
      // @graph. Keep unrelated graph nodes (for example Dataset), but let the
      // shared composer own these canonical page schemas so nested duplicates
      // cannot survive an otherwise clean top-level dedupe.
      if (Array.isArray(obj["@graph"])) {
        const graph = (obj["@graph"] as JsonLd[]).filter((entry) => {
          if (!entry || typeof entry !== "object") return true;
          const graphType = typeOf(entry);
          return (
            graphType !== "FAQPage" &&
            graphType !== "BreadcrumbList" &&
            !isArticleLike(entry)
          );
        });
        if (graph.length > 0) out.push({ ...obj, "@graph": graph });
        continue;
      }
      // Always derive BreadcrumbList so every item receives a canonical URL.
      if (t === "BreadcrumbList") continue;
      // FAQ is always derived from page.faq so visible copy and schema match.
      if (t === "FAQPage") continue;
      if (isArticleLike(obj)) {
        if (keptArticle) continue;
        keptArticle = true;
      }
      if (t) engineTypes.add(t);
      out.push(
        isArticleLike(obj)
          ? enrichArticleObject(obj, page, siteOrigin, localizedCanonical)
          : obj,
      );
    }
  }

  // 2. Every /r briefing is an article. If the engine sent no Article-like
  // schema, synthesise one and enrich it with canonical metadata.
  if (!out.some(isArticleLike)) {
    out.push(
      enrichArticleObject(
        { "@type": "Article", headline: page.h1 ?? page.title },
        page,
        siteOrigin,
        localizedCanonical,
      ),
    );
    engineTypes.add("Article");
  }

  // Helper: only push a derived schema if the engine didn't already provide
  // one of the same @type.
  const pushUnlessProvided = (obj: JsonLd | null) => {
    if (!obj) return;
    const t = typeOf(obj);
    if (t && engineTypes.has(t)) return;
    out.push(obj);
  };

  // 3. FAQPage
  pushUnlessProvided(buildFaqPageJsonLd(page.faq));

  // 4. BreadcrumbList — pass localizePath so internal breadcrumb hrefs are
  //    rewritten to the active locale (e.g. "/r" -> "/fr/r").
  pushUnlessProvided(
    buildBreadcrumbJsonLd(
      breadcrumbs ?? null,
      siteOrigin,
      currentPath,
      localizePath,
    ),
  );

  // 5. Dataset (only for property_detail — these pages ARE the data)
  if (page.pageType === "property_detail") {
    pushUnlessProvided(
      buildDatasetForProperty(page, siteOrigin, localizedCanonical),
    );
  }

  // 6. QAPage (only for answer — these pages ARE a single Q&A unit)
  if (page.pageType === "answer") {
    pushUnlessProvided(buildQaPageJsonLd(page, siteOrigin, localizedCanonical));
  }

  return out;
}

// ---------- RealEstateListing (property detail pages) ----------
//
// Schema.org RealEstateListing is the canonical type for individual property
// listings. It augments the Dataset schema already emitted for property_detail
// pages with structured price, floor-size, and address fields — making the
// page directly eligible for Google's rich results for real-estate listings.

export function buildRealEstateListingJsonLd({
  id,
  title,
  askingPriceMad,
  builtSurfaceM2,
  district,
  siteOrigin,
  canonicalPath,
}: {
  id: number;
  title?: string | null;
  askingPriceMad?: number | null;
  builtSurfaceM2?: number | null;
  district?: string | null;
  siteOrigin: string;
  /** Locale-prefixed path, e.g. "/properties/123" or "/fr/properties/123". */
  canonicalPath: string;
}): JsonLd {
  const origin = siteOrigin.replace(/\/$/, "");
  const url = `${origin}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`;

  const schema: JsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": url,
    name: title ?? `Riad for sale — property #${id}`,
    url,
    description: `Investment analysis for ${title ?? `riad #${id}`}${district ? ` in ${district}, Marrakech Medina` : " in Marrakech Medina"}. Includes pricing intelligence, renovation estimate, and best-use analysis.`,
    publisher: { "@type": "Organization", name: "RiadIntel", url: origin },
  };

  if (typeof askingPriceMad === "number" && askingPriceMad > 0) {
    schema.price = askingPriceMad;
    schema.priceCurrency = "MAD";
  }

  if (typeof builtSurfaceM2 === "number" && builtSurfaceM2 > 0) {
    schema.floorSize = {
      "@type": "QuantitativeValue",
      value: builtSurfaceM2,
      unitCode: "MTK",
    };
  }

  schema.address = {
    "@type": "PostalAddress",
    addressLocality: district ? `${district}, Marrakech Medina` : "Marrakech Medina",
    addressRegion: "Marrakech-Safi",
    addressCountry: "MA",
  };

  return schema;
}

// ---------- Dataset (market benchmarks / district pages) ----------
//
// Describes the aggregate district price-benchmark dataset shown on the
// Market Benchmarks page. Injected client-side once the summary data loads.

export function buildMarketBenchmarksDatasetJsonLd({
  siteOrigin,
  asOf,
}: {
  siteOrigin: string;
  asOf?: string | null;
}): JsonLd {
  const origin = siteOrigin.replace(/\/$/, "");
  const url = `${origin}/market-benchmarks`;
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "@id": url,
    name: "Marrakech Medina Riad Price Benchmarks by District",
    description:
      "Median asking price per m², listing counts, and data confidence by district for Marrakech Medina riads — compiled from live listings by RiadIntel.",
    url,
    isAccessibleForFree: true,
    creator: { "@type": "Organization", name: "RiadIntel", url: origin },
    publisher: { "@type": "Organization", name: "RiadIntel", url: origin },
    license: `${origin}/terms`,
    keywords: [
      "Marrakech riad",
      "riad investment",
      "price per m2",
      "district benchmarks",
      "Marrakech medina",
    ],
    variableMeasured: [
      "Median asking price per m² (MAD)",
      "Listing count",
      "District data confidence",
    ],
    ...(asOf ? { temporalCoverage: asOf.slice(0, 10) } : {}),
  };
}

// Re-export so callers don't need a second import.
export { DATA_PERIOD };
